export const microInteraction = {
  statusBadge: 'transition-[background-color,border-color,color] duration-base ease-out',
  avatar:
    'transition-[box-shadow,transform] duration-base ease-out motion-safe:hover:-translate-y-px hover:ring-2 hover:ring-primary/20 focus-visible:ring-2 focus-visible:ring-ring',
  countBadge:
    'transition-[background-color,border-color,color,box-shadow,transform] duration-base ease-out motion-safe:hover:-translate-y-px motion-safe:hover:shadow-sm',
  checkbox:
    'transition-[background-color,border-color,color,box-shadow,transform] duration-fast ease-out motion-safe:hover:scale-105',
  // The hover lift (`motion-safe:hover:-translate-y-px active:translate-y-0`)
  // shipped here originally and was reverted on designer feedback: the Approve
  // button shifting up on hover read as inconsistent with sibling buttons. Only
  // the color/border/box-shadow transition remains, so the Approve state still
  // has a smooth transition when it flips approved ↔ not-approved.
  approveAction:
    'transition-[background-color,border-color,color,box-shadow] duration-base ease-out',
  // The animation composes its timing from tokens rather than a literal (it was
  // `micro-check-in_180ms_ease-out`). Reduced motion works by zeroing
  // `--duration-*`, which cannot reach a literal inside an `animation` shorthand,
  // so the old form kept playing at 180ms for a user who asked for less and only
  // the `motion-safe:` prefix prevented it. Both guards are kept deliberately.
  approveIcon:
    'transition-[opacity,transform] duration-fast ease-out motion-safe:animate-[micro-check-in_var(--duration-fast)_var(--ease-out)]',
  tabTrigger:
    'transition-[background-color,color,box-shadow,transform] duration-base ease-out motion-safe:data-[state=active]:scale-[1.02]',
  // The plus is rotationally symmetric, so a partial turn would be invisible —
  // the affordance needs a full revolution to read as motion at all. A
  // transition (not a keyframe animation) so it reverses smoothly back to
  // 0deg on mouse leave instead of snapping back. `rotate`, not `transform`:
  // Tailwind v4's `rotate-*` utility sets the standalone CSS `rotate`
  // property, which a `transition-[...transform]` list never picks up.
  // Goes on the icon and reads hover off the trigger via `group`: a
  // `transition-[...]` utility on the trigger itself replaces the Tessra
  // Button's own transition, which is retiming a design-system component.
  shortcutAddIcon:
    'transition-[rotate] duration-slow ease-out motion-safe:group-hover:rotate-[360deg]',
  breadcrumbLink: 'transition-colors duration-base ease-out hover:text-foreground',
  breadcrumbPage: 'transition-colors duration-base ease-out',
  breadcrumbSeparator: 'transition-opacity duration-base ease-out',
  // A shortcut slot's idle content (icon + label/title) rises in whenever it
  // replaces another state — swapping in from the picker, or from nothing on
  // first mount — rather than snapping into place.
  shortcutContentIn:
    'motion-safe:animate-[shortcut-content-in_var(--duration-base)_var(--ease-out)]',
  // The picker's own content reveals in two beats: the Cancel/Save row first,
  // the label + Select a moment behind it. `both` holds the pre-animation
  // (invisible) state through the delay so the row doesn't flash visible
  // before it's meant to animate in.
  shortcutContentInButtons:
    'motion-safe:animate-[shortcut-content-in_var(--duration-base)_var(--ease-out)_40ms_both]',
  shortcutContentInField:
    'motion-safe:animate-[shortcut-content-in_var(--duration-base)_var(--ease-out)_160ms_both]',
  // A confirmed shortcut settles into its tile with a small spring overshoot
  // instead of appearing instantly.
  shortcutPopIn: 'motion-safe:animate-[shortcut-pop-in_var(--duration-slow)_var(--ease-spring)]',
  // The mirror of shortcutPopIn: plays while a removed shortcut's tile is
  // still mounted, so the delete only actually happens once it's finished.
  // `forwards` holds the faded-out end state, because the removal round trip
  // outlasts the animation — without it the tile snaps back to full opacity
  // and sits there until the request lands.
  shortcutOut:
    'motion-safe:animate-[shortcut-out_var(--duration-fast)_var(--ease-in-out)_forwards]',
  // Click feedback on the confirm/add button — a squish, separate from the
  // tile's own entrance animation once the shortcut is saved.
  shortcutRecoil: 'motion-safe:animate-[shortcut-recoil_var(--duration-slower)_var(--ease-spring)]',
} as const;
